import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { API, getAuthHeader, formatDateTimeDDMMYYYY, getTodayDDMMYYYY } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { ChatOccurrence, ChatTargetSelect } from "@/components/recurring/RecurringChatBits";
import { 
  Plus, Repeat, Calendar, Clock, Bell, Mail, MessageSquare, Smartphone,
  Trash2, Edit, Play, Pause, ChevronRight, AlertCircle, CheckCircle2, User,
  Search, Filter, X, RotateCcw
} from "lucide-react";

const DAYS_OF_WEEK = [
  { value: 0, label: "Mon" },
  { value: 1, label: "Tue" },
  { value: 2, label: "Wed" },
  { value: 3, label: "Thu" },
  { value: 4, label: "Fri" },
  { value: 5, label: "Sat" },
  { value: 6, label: "Sun" }
];

const MONTHS = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" }
];

const CATEGORIES = [
  { value: "general", label: "General", color: "bg-slate-100 text-slate-700" },
  { value: "meeting", label: "Meeting", color: "bg-blue-100 text-blue-700" },
  { value: "report", label: "Report", color: "bg-purple-100 text-purple-700" },
  { value: "review", label: "Review", color: "bg-green-100 text-green-700" },
  { value: "deadline", label: "Deadline", color: "bg-red-100 text-red-700" },
  { value: "reminder", label: "Reminder", color: "bg-yellow-100 text-yellow-700" }
];

const RecurringTasksPanel = ({ user }) => {
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  
  // Search and Filter states
  const [searchQuery, setSearchQuery] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    recurrence_type: "all",
    status: "all",
    priority: "all",
    category: "all",
    assigned_to: "all",
    reminder_type: "all"
  });
  
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    assigned_to: "",
    priority: "medium",
    category: "general",
    recurrence_type: "daily",
    recurrence_days: [0], // Monday default
    recurrence_day_of_month: 1,
    recurrence_month: 1,
    recurrence_date: 1,
    start_date: getTodayDDMMYYYY(),
    end_date: "",
    reminder_enabled: true,
    reminder_time: "09:00",
    reminder_minutes_before: 30,
    reminder_popup: true,
    reminder_email: false,
    reminder_whatsapp: false,
    reminder_chat: true,
    chat_target: "",
    whatsapp_number: "",
    email: ""
  });

  useEffect(() => {
    fetchData();
  }, []);

  const [chatSt, setChatSt] = useState({});
  const fetchData = async () => {
    try {
      const [tasksRes, usersRes, stRes] = await Promise.all([
        axios.get(`${API}/recurring-tasks`, { headers: getAuthHeader() }),
        axios.get(`${API}/users`, { headers: getAuthHeader() }),
        axios.get(`${API}/recurring-chat/status`, { headers: getAuthHeader() }).catch(() => ({ data: {} }))
      ]);
      setTasks(tasksRes.data);
      setUsers(usersRes.data);
      setChatSt(stRes.data || {});
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  // Filtered tasks based on search and filters
  const filteredTasks = useMemo(() => {
    return tasks.filter(task => {
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch = 
          task.title.toLowerCase().includes(query) ||
          (task.description && task.description.toLowerCase().includes(query)) ||
          (task.assigned_to_name && task.assigned_to_name.toLowerCase().includes(query)) ||
          task.category.toLowerCase().includes(query);
        if (!matchesSearch) return false;
      }

      // Recurrence type filter
      if (filters.recurrence_type !== "all" && task.recurrence_type !== filters.recurrence_type) {
        return false;
      }

      // Status filter
      if (filters.status !== "all" && task.status !== filters.status) {
        return false;
      }

      // Priority filter
      if (filters.priority !== "all" && task.priority !== filters.priority) {
        return false;
      }

      // Category filter
      if (filters.category !== "all" && task.category !== filters.category) {
        return false;
      }

      // Assigned to filter
      if (filters.assigned_to !== "all") {
        if (filters.assigned_to === "unassigned" && task.assigned_to) {
          return false;
        } else if (filters.assigned_to !== "unassigned" && task.assigned_to !== filters.assigned_to) {
          return false;
        }
      }

      // Reminder type filter
      if (filters.reminder_type !== "all") {
        switch (filters.reminder_type) {
          case "popup":
            if (!task.reminder_popup) return false;
            break;
          case "chat":
            if (!task.reminder_chat) return false;
            break;
          case "email":
            if (!task.reminder_email) return false;
            break;
          case "whatsapp":
            if (!task.reminder_whatsapp) return false;
            break;
          case "none":
            if (task.reminder_popup || task.reminder_chat || task.reminder_email || task.reminder_whatsapp) return false;
            break;
        }
      }

      return true;
    });
  }, [tasks, searchQuery, filters]);

  // Check if any filter is active
  const hasActiveFilters = useMemo(() => {
    return searchQuery || 
      filters.recurrence_type !== "all" ||
      filters.status !== "all" ||
      filters.priority !== "all" ||
      filters.category !== "all" ||
      filters.assigned_to !== "all" ||
      filters.reminder_type !== "all";
  }, [searchQuery, filters]);

  // Reset all filters
  const resetFilters = () => {
    setSearchQuery("");
    setFilters({
      recurrence_type: "all",
      status: "all",
      priority: "all",
      category: "all",
      assigned_to: "all",
      reminder_type: "all"
    });
  };

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      assigned_to: "",
      priority: "medium",
      category: "general",
      recurrence_type: "daily",
      recurrence_days: [0],
      recurrence_day_of_month: 1,
      recurrence_month: 1,
      recurrence_date: 1,
      start_date: getTodayDDMMYYYY(),
      end_date: "",
      reminder_enabled: true,
      reminder_time: "09:00",
      reminder_minutes_before: 30,
      reminder_popup: true,
      reminder_email: false,
      reminder_whatsapp: false,
      reminder_chat: true,
      chat_target: "",
      whatsapp_number: "",
      email: ""
    });
    setEditingTask(null);
  };

  const handleOpenCreate = () => {
    resetForm();
    setDialogOpen(true);
  };

  const handleOpenEdit = (task) => {
    setEditingTask(task);
    setFormData({
      title: task.title,
      description: task.description || "",
      assigned_to: task.assigned_to || "",
      priority: task.priority,
      category: task.category,
      recurrence_type: task.recurrence_type,
      recurrence_days: task.recurrence_days || [0],
      recurrence_day_of_month: task.recurrence_day_of_month || 1,
      recurrence_month: task.recurrence_month || 1,
      recurrence_date: task.recurrence_date || 1,
      start_date: task.start_date,
      end_date: task.end_date || "",
      reminder_enabled: task.reminder_enabled,
      reminder_time: task.reminder_time,
      reminder_minutes_before: task.reminder_minutes_before,
      reminder_popup: task.reminder_popup,
      reminder_email: task.reminder_email,
      reminder_whatsapp: task.reminder_whatsapp,
      reminder_chat: task.reminder_chat,
      chat_target: task.chat_target || "",
      whatsapp_number: task.whatsapp_number || "",
      email: task.email || ""
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTask) {
        await axios.put(`${API}/recurring-tasks/${editingTask.id}`, formData, { headers: getAuthHeader() });
        toast.success("Recurring task updated!");
      } else {
        await axios.post(`${API}/recurring-tasks`, formData, { headers: getAuthHeader() });
        toast.success("Recurring task created!");
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operation failed");
    }
  };

  const handleDelete = async () => {
    if (!taskToDelete) return;
    try {
      await axios.delete(`${API}/recurring-tasks/${taskToDelete.id}`, { headers: getAuthHeader() });
      toast.success("Recurring task deleted!");
      setDeleteConfirmOpen(false);
      setTaskToDelete(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to delete task");
    }
  };

  const handleToggleStatus = async (task) => {
    const newStatus = task.status === 'active' ? 'paused' : 'active';
    try {
      await axios.put(`${API}/recurring-tasks/${task.id}`, { status: newStatus }, { headers: getAuthHeader() });
      toast.success(`Task ${newStatus === 'active' ? 'activated' : 'paused'}`);
      fetchData();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const handleTriggerReminder = async (task) => {
    try {
      const response = await axios.post(`${API}/recurring-tasks/${task.id}/trigger-reminder`, {}, { headers: getAuthHeader() });
      toast.success(`Reminders triggered: ${response.data.types.join(', ')}`);
    } catch (error) {
      toast.error("Failed to trigger reminders");
    }
  };

  const toggleDay = (day) => {
    const days = formData.recurrence_days.includes(day)
      ? formData.recurrence_days.filter(d => d !== day)
      : [...formData.recurrence_days, day];
    setFormData({ ...formData, recurrence_days: days.length > 0 ? days : [0] });
  };

  const getRecurrenceLabel = (task) => {
    switch (task.recurrence_type) {
      case 'daily': return 'Daily';
      case 'weekly': 
        const days = (task.recurrence_days || []).map(d => DAYS_OF_WEEK.find(dw => dw.value === d)?.label).join(', ');
        return `Weekly (${days})`;
      case 'monthly': return `Monthly (Day ${task.recurrence_day_of_month || 1})`;
      case 'yearly': 
        const month = MONTHS.find(m => m.value === task.recurrence_month)?.label || 'Jan';
        return `Yearly (${month} ${task.recurrence_date || 1})`;
      default: return task.recurrence_type;
    }
  };

  const getCategoryColor = (category) => {
    const cat = CATEGORIES.find(c => c.value === category);
    return cat?.color || "bg-slate-100 text-slate-700";
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-700';
      case 'medium': return 'bg-orange-100 text-orange-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return formatDateTimeDDMMYYYY(dateStr);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg">
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Repeat className="w-5 h-5 text-purple-600" />
                Recurring Tasks
              </CardTitle>
              <CardDescription>Manage repeating tasks with automatic reminders</CardDescription>
            </div>
            <Button onClick={handleOpenCreate} className="bg-purple-600 hover:bg-purple-700">
              <Plus className="w-4 h-4 mr-2" />
              Create Recurring Task
            </Button>
          </div>

          {/* Search and Filter Bar */}
          <div className="mt-4 space-y-3">
            <div className="flex flex-col sm:flex-row gap-3">
              {/* Search Input */}
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search by title, description, assignee..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-10"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              
              {/* Filter Toggle Button */}
              <Button
                variant={showFilters ? "default" : "outline"}
                onClick={() => setShowFilters(!showFilters)}
                className={showFilters ? "bg-purple-600 hover:bg-purple-700" : ""}
              >
                <Filter className="w-4 h-4 mr-2" />
                Filters
                {hasActiveFilters && (
                  <Badge className="ml-2 bg-purple-100 text-purple-700 text-xs">Active</Badge>
                )}
              </Button>

              {/* Reset Filters Button */}
              {hasActiveFilters && (
                <Button variant="ghost" onClick={resetFilters} className="text-slate-600">
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Reset
                </Button>
              )}
            </div>

            {/* Filter Dropdowns */}
            {showFilters && (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 p-4 bg-slate-50 rounded-lg">
                {/* Recurrence Type Filter */}
                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">Recurrence</Label>
                  <Select value={filters.recurrence_type} onValueChange={(val) => setFilters({...filters, recurrence_type: val})}>
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      <SelectItem value="daily">Daily</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="yearly">Yearly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Status Filter */}
                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">Status</Label>
                  <Select value={filters.status} onValueChange={(val) => setFilters({...filters, status: val})}>
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="paused">Paused</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Priority Filter */}
                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">Priority</Label>
                  <Select value={filters.priority} onValueChange={(val) => setFilters({...filters, priority: val})}>
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Priorities</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="low">Low</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Category Filter */}
                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">Category</Label>
                  <Select value={filters.category} onValueChange={(val) => setFilters({...filters, category: val})}>
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Categories</SelectItem>
                      {CATEGORIES.map(cat => (
                        <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Assigned To Filter */}
                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">Assigned To</Label>
                  <Select value={filters.assigned_to} onValueChange={(val) => setFilters({...filters, assigned_to: val})}>
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Users</SelectItem>
                      <SelectItem value="unassigned">Unassigned</SelectItem>
                      {users.map(u => (
                        <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Reminder Type Filter */}
                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">Reminder</Label>
                  <Select value={filters.reminder_type} onValueChange={(val) => setFilters({...filters, reminder_type: val})}>
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Reminders</SelectItem>
                      <SelectItem value="popup">Popup</SelectItem>
                      <SelectItem value="chat">Chat</SelectItem>
                      <SelectItem value="email">Email</SelectItem>
                      <SelectItem value="whatsapp">WhatsApp</SelectItem>
                      <SelectItem value="none">No Reminder</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {/* Results Count */}
            {hasActiveFilters && (
              <div className="text-sm text-slate-500">
                Showing {filteredTasks.length} of {tasks.length} tasks
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {tasks.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Repeat className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p className="font-medium">No recurring tasks yet</p>
              <p className="text-sm">Create your first recurring task to automate reminders</p>
            </div>
          ) : filteredTasks.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Search className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p className="font-medium">No tasks match your filters</p>
              <p className="text-sm mb-4">Try adjusting your search or filter criteria</p>
              <Button variant="outline" onClick={resetFilters}>
                <RotateCcw className="w-4 h-4 mr-2" />
                Reset Filters
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredTasks.map(task => (
                <div 
                  key={task.id} 
                  className={`p-4 rounded-xl border ${task.status === 'paused' ? 'bg-slate-50 border-slate-200' : 'bg-white border-slate-200'} hover:shadow-md transition-shadow`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="font-semibold text-lg">{task.title}</h4>
                        {task.status === 'paused' && (
                          <Badge variant="outline" className="text-xs">Paused</Badge>
                        )}
                      </div>
                      
                      {task.description && (
                        <p className="text-slate-600 text-sm mb-3">{task.description}</p>
                      )}
                      
                      <div className="flex flex-wrap gap-2 mb-3">
                        <Badge className={getCategoryColor(task.category)}>{task.category}</Badge>
                        <Badge className={getPriorityColor(task.priority)}>{task.priority}</Badge>
                        <Badge variant="outline" className="flex items-center gap-1">
                          <Repeat className="w-3 h-3" />
                          {getRecurrenceLabel(task)}
                        </Badge>
                        <Badge variant="outline" className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {task.reminder_time}
                        </Badge>
                      </div>
                      
                      <div className="flex flex-wrap gap-4 text-sm text-slate-500">
                        {task.assigned_to_name && (
                          <span className="flex items-center gap-1">
                            <User className="w-4 h-4" />
                            {task.assigned_to_name}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          Next: {formatDateTime(task.next_trigger)}
                        </span>
                        
                        {/* Reminder indicators */}
                        <div className="flex items-center gap-2">
                          {task.reminder_popup && <Bell className="w-4 h-4 text-purple-500" title="Popup" />}
                          {task.reminder_email && <Mail className="w-4 h-4 text-blue-500" title="Email" />}
                          {task.reminder_whatsapp && <Smartphone className="w-4 h-4 text-green-500" title="WhatsApp" />}
                          {task.reminder_chat && <MessageSquare className="w-4 h-4 text-orange-500" title="Chat" />}
                        </div>
                      </div>
                      <ChatOccurrence task={task} st={chatSt[task.id]} onChanged={fetchData} />
                    </div>
                    
                    <div className="flex items-center gap-2 ml-4">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleTriggerReminder(task)}
                        title="Send reminder now"
                      >
                        <Bell className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleToggleStatus(task)}
                        title={task.status === 'active' ? 'Pause' : 'Activate'}
                      >
                        {task.status === 'active' ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleOpenEdit(task)}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => { setTaskToDelete(task); setDeleteConfirmOpen(true); }}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingTask ? 'Edit Recurring Task' : 'Create Recurring Task'}</DialogTitle>
            <DialogDescription>
              Set up a task that repeats automatically with reminders
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Info */}
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Title *</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Task title"
                    required
                  />
                </div>
                
                <div className="col-span-2">
                  <Label>Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Task description"
                    rows={2}
                  />
                </div>
                
                <div>
                  <Label>Assign To</Label>
                  <Select value={formData.assigned_to || "unassigned"} onValueChange={(val) => setFormData({ ...formData, assigned_to: val === "unassigned" ? "" : val })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select user" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unassigned">Unassigned</SelectItem>
                      {users.map(u => (
                        <SelectItem key={u.id} value={u.id}>{u.name} ({u.team})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>Priority</Label>
                  <Select value={formData.priority} onValueChange={(val) => setFormData({ ...formData, priority: val })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>Category</Label>
                  <Select value={formData.category} onValueChange={(val) => setFormData({ ...formData, category: val })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map(cat => (
                        <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <ChatTargetSelect value={formData.chat_target} users={users} onChange={(v) => setFormData({ ...formData, chat_target: v })} />
              </div>
            </div>
            
            {/* Recurrence Settings */}
            <div className="space-y-4 p-4 bg-slate-50 rounded-lg">
              <h4 className="font-medium flex items-center gap-2">
                <Repeat className="w-4 h-4" />
                Recurrence Pattern
              </h4>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Repeat</Label>
                  <Select value={formData.recurrence_type} onValueChange={(val) => setFormData({ ...formData, recurrence_type: val })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">Daily</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="yearly">Yearly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>Start Date</Label>
                  <Input
                    type="text"
                    placeholder="dd/mm/yyyy"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    required
                  />
                </div>
              </div>
              
              {/* Weekly - Day selection */}
              {formData.recurrence_type === 'weekly' && (
                <div>
                  <Label className="mb-2 block">Days of Week</Label>
                  <div className="flex gap-2">
                    {DAYS_OF_WEEK.map(day => (
                      <button
                        key={day.value}
                        type="button"
                        onClick={() => toggleDay(day.value)}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                          formData.recurrence_days.includes(day.value)
                            ? 'bg-purple-600 text-white'
                            : 'bg-white border border-slate-200 hover:bg-slate-50'
                        }`}
                      >
                        {day.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Monthly - Day of month */}
              {formData.recurrence_type === 'monthly' && (
                <div>
                  <Label>Day of Month</Label>
                  <Select 
                    value={String(formData.recurrence_day_of_month)} 
                    onValueChange={(val) => setFormData({ ...formData, recurrence_day_of_month: parseInt(val) })}
                  >
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                        <SelectItem key={day} value={String(day)}>{day}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              {/* Yearly - Month and Day */}
              {formData.recurrence_type === 'yearly' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Month</Label>
                    <Select 
                      value={String(formData.recurrence_month)} 
                      onValueChange={(val) => setFormData({ ...formData, recurrence_month: parseInt(val) })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {MONTHS.map(month => (
                          <SelectItem key={month.value} value={String(month.value)}>{month.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Day</Label>
                    <Select 
                      value={String(formData.recurrence_date)} 
                      onValueChange={(val) => setFormData({ ...formData, recurrence_date: parseInt(val) })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                          <SelectItem key={day} value={String(day)}>{day}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
              
              <div>
                <Label>End Date (Optional)</Label>
                <Input
                  type="text"
                  placeholder="dd/mm/yyyy"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                />
              </div>
            </div>
            
            {/* Reminder Settings */}
            <div className="space-y-4 p-4 bg-slate-50 rounded-lg">
              <div className="flex items-center justify-between">
                <h4 className="font-medium flex items-center gap-2">
                  <Bell className="w-4 h-4" />
                  Reminder Settings
                </h4>
                <Switch
                  checked={formData.reminder_enabled}
                  onCheckedChange={(checked) => setFormData({ ...formData, reminder_enabled: checked })}
                />
              </div>
              
              {formData.reminder_enabled && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Reminder Time</Label>
                      <Input
                        type="time"
                        value={formData.reminder_time}
                        onChange={(e) => setFormData({ ...formData, reminder_time: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>Minutes Before</Label>
                      <Select 
                        value={String(formData.reminder_minutes_before)} 
                        onValueChange={(val) => setFormData({ ...formData, reminder_minutes_before: parseInt(val) })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="0">At task time</SelectItem>
                          <SelectItem value="5">5 minutes before</SelectItem>
                          <SelectItem value="15">15 minutes before</SelectItem>
                          <SelectItem value="30">30 minutes before</SelectItem>
                          <SelectItem value="60">1 hour before</SelectItem>
                          <SelectItem value="1440">1 day before</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <Label>Notification Channels</Label>
                    
                    <div className="flex items-center gap-3">
                      <Checkbox
                        checked={formData.reminder_popup}
                        onCheckedChange={(checked) => setFormData({ ...formData, reminder_popup: checked })}
                      />
                      <Bell className="w-4 h-4 text-purple-500" />
                      <span className="text-sm">Popup on login</span>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <Checkbox
                        checked={formData.reminder_chat}
                        onCheckedChange={(checked) => setFormData({ ...formData, reminder_chat: checked })}
                      />
                      <MessageSquare className="w-4 h-4 text-orange-500" />
                      <span className="text-sm">In-app chat notification</span>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <Checkbox
                        checked={formData.reminder_email}
                        onCheckedChange={(checked) => setFormData({ ...formData, reminder_email: checked })}
                      />
                      <Mail className="w-4 h-4 text-blue-500" />
                      <span className="text-sm">Email notification (mocked)</span>
                    </div>
                    
                    {formData.reminder_email && (
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="Email address"
                        className="ml-7"
                      />
                    )}
                    
                    <div className="flex items-center gap-3">
                      <Checkbox
                        checked={formData.reminder_whatsapp}
                        onCheckedChange={(checked) => setFormData({ ...formData, reminder_whatsapp: checked })}
                      />
                      <Smartphone className="w-4 h-4 text-green-500" />
                      <span className="text-sm">WhatsApp notification (Twilio)</span>
                    </div>
                    
                    {formData.reminder_whatsapp && (
                      <Input
                        type="tel"
                        value={formData.whatsapp_number}
                        onChange={(e) => setFormData({ ...formData, whatsapp_number: e.target.value })}
                        placeholder="+1234567890 (with country code)"
                        className="ml-7"
                      />
                    )}
                  </div>
                </>
              )}
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-purple-600 hover:bg-purple-700">
                {editingTask ? 'Update Task' : 'Create Task'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Recurring Task?</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{taskToDelete?.title}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete}>
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RecurringTasksPanel;
